import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const TYPES = ["All", "Brochure", "Datasheet", "Catalogue", "Guide", "Technical Document"];

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [type, setType] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => { api.get("/resources").then((r) => setResources(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => resources.filter((r) => {
    const matchType = type === "All" || r.type === type;
    const q = query.trim().toLowerCase();
    const matchQ = !q || r.title.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q);
    return matchType && matchQ;
  }), [resources, type, query]);

  return (
    <div data-testid="resources-page">
      <SEO title="Resources & Downloads" path="/resources"
        description="Datasheets, brochures, technical documents and guides from STJ Innovations — cleanroom equipment and precision fabrication resources." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={[{ label: "Resources" }]} />
          <p className="eyebrow eyebrow-light">Resources</p>
          <KineticLines lines={["Technical Resources", "& Downloads"]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            Datasheets, brochures and technical documents. Documents marked "on request" are shared directly by our team with the latest revision.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter resources by type">
            {TYPES.map((t) => (
              <button key={t} data-testid={`resource-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                role="tab" aria-selected={type === t} onClick={() => setType(t)}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${type === t ? "border-accent bg-accent text-white" : "border-line bg-white text-steel hover:border-accent/50"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="relative sm:w-72">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="resource-search-input" type="search" placeholder="Search resources…"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="input-field pl-9" aria-label="Search resources" />
          </div>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="resources-grid">
          {filtered.map((r, i) => (
            <Reveal key={r.slug || i} delay={(i % 2) * 0.06}>
              <article className="flex h-full items-start gap-5 border border-line bg-white p-7" data-testid={`resource-card-${r.slug}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-line bg-mist text-accent">
                  <FileText size={19} />
                </span>
                <div className="flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{r.type}</p>
                  <h3 className="mt-2 font-display text-lg font-semibold text-ink">{r.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{r.description}</p>
                  {r.file_url ? (
                    <a href={r.file_url} data-testid={`resource-download-${r.slug}`}
                      onClick={() => trackEvent("datasheet_download", { resource: r.slug })}
                      className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-accent hover:text-accent-dark">
                      Download <ArrowRight size={12} />
                    </a>
                  ) : (
                    <Link to={`/request-a-quote?message=${encodeURIComponent(`Please share: ${r.title}`)}`}
                      data-testid={`resource-request-${r.slug}`}
                      onClick={() => trackEvent("datasheet_download", { resource: r.slug })}
                      className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-accent hover:text-accent-dark">
                      {r.note || "Available on request"} <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="mt-10 border border-line bg-mist p-10 text-center text-sm text-steel" data-testid="resources-empty">
            No resources match your filter. Try a different search.
          </p>
        )}
      </section>
      <CTASection />
    </div>
  );
}
